import React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { ActivityTypeEnum, ApprovalWorkflowStatusEnum } from "../schema";
import styles from "../components/FeedUpdate/FeedUpdate.module.scss";
import { OpenExternalIcon } from "../theme/svg/OpenExternalIcon";
import { SwimlaneId } from "../constants";
import { formatDatetime } from "./formatDatetime";
import { assertUnreachable } from "./assertUnreachable";
import { formatReplaceUnderscores } from "./formatReplaceUnderscores";
import { getCodePrefixBySwimlaneId } from "./getCodePrefixBySwimlaneId";
import { ActivityFeedItemFragmentWithOptionalAttachments } from "./getNotificationInfo";

export function getNotificationMessageByActivity(
  _organizationUrlName: string,
  _history: RouteComponentProps["history"] | undefined,
  activity: ActivityFeedItemFragmentWithOptionalAttachments,
) {
  if (!activity.data || !activity.data.__typename) {
    return null;
  }

  switch (activity.data.__typename) {
    case ActivityTypeEnum.ASSIGN: {
      const { assigneeInfo, itemInfo } = activity.data;

      // fail if no itemInfo is present
      if (!itemInfo || (itemInfo && !itemInfo.code)) {
        return null;
      }

      return (
        <>
          {assigneeInfo ? (
            <>
              Assigned <strong>{itemInfo.code}</strong> “{itemInfo.name}“ to{" "}
              <span
                style={{ color: "#00bf3a" }}
              >{`${assigneeInfo.firstName} ${assigneeInfo.lastName}`}</span>
            </>
          ) : (
            <>
              Marked <strong>{itemInfo.code}</strong> “{itemInfo.name}“ to be{" "}
              <span style={{ color: "#00bf3a" }}>Unassigned</span>
            </>
          )}
        </>
      );
    }

    case ActivityTypeEnum.DRAG_CARD: {
      const messageData = activity.data;

      return (
        <>
          {[SwimlaneId.PURCHASE_REQUEST, SwimlaneId.RFX].includes(
            SwimlaneId[messageData.fromType],
          ) ? (
            <div>
              Started{" "}
              <strong>
                {formatReplaceUnderscores(
                  getCodePrefixBySwimlaneId(SwimlaneId[messageData.toType]),
                )}
              </strong>{" "}
              by dragging card{" "}
              <strong>
                {formatReplaceUnderscores(
                  getCodePrefixBySwimlaneId(SwimlaneId[messageData.fromType]),
                )}
                -{messageData.fromCode}
              </strong>
            </div>
          ) : (
            <div>
              Moved card from{" "}
              <strong>{formatReplaceUnderscores(messageData.fromType)}</strong>{" "}
              to <strong>{formatReplaceUnderscores(messageData.toType)}</strong>
            </div>
          )}
        </>
      );
    }

    case ActivityTypeEnum.COMMENT: {
      const messageData = activity.data;
      const code =
        messageData.relativeInfo.__typename === "ActivityRelativeCardInfo"
          ? messageData.relativeInfo.code
          : undefined;

      if (!code) {
        return <>created new comment.</>;
      }

      return (
        <>
          commented on <span style={{ color: "#00bf3a" }}>{code}</span>
        </>
      );
    }

    case ActivityTypeEnum.REPLY: {
      const messageData = activity.data;
      const code =
        messageData.relativeInfo.__typename === "ActivityRelativeCardInfo"
          ? messageData.relativeInfo.code
          : undefined;

      return (
        <>
          <span>replied to comment </span>
          {code && (
            <>
              on <span style={{ color: "#00bf3a" }}>{code}</span>
            </>
          )}
        </>
      );
    }

    case ActivityTypeEnum.CREATE_PURCHASE_REQUEST: {
      const messageData = activity.data;

      return (
        <>
          <span>created a new purchase request card </span>
          <span style={{ color: "#00bf3a" }}>PR-{messageData.code}</span>
        </>
      );
    }

    case ActivityTypeEnum.CREATE_PURCHASE_REQUEST_PRODUCT: {
      const messageData = activity.data;

      return (
        <>
          Added a new purchase request product item{" "}
          <strong>{messageData.name}</strong> to{" "}
          <strong>PR-{messageData.purchaseRequestCode}</strong>
        </>
      );
    }

    case ActivityTypeEnum.CREATE_PURCHASE_REQUEST_SERVICE: {
      const messageData = activity.data;

      return (
        <>
          Added a new purchase request for service{" "}
          <strong>{messageData.name}</strong>
        </>
      );
    }

    case ActivityTypeEnum.EMAIL_INBOUND_MENTION: {
      const code =
        activity.relativeInfo &&
        activity.relativeInfo.__typename === "ActivityRelativeCardInfo"
          ? activity.relativeInfo.code
          : undefined;

      return (
        <>
          <span>replied to comment </span>
          {code && (
            <>
              on <span style={{ color: "#00bf3a" }}>{code}</span> by email
            </>
          )}
        </>
      );
    }

    case ActivityTypeEnum.EMAIL_INBOUND_SUPPLIER: {
      const code =
        activity.relativeInfo?.__typename === "ActivityRelativeCardInfo"
          ? activity.relativeInfo.code
          : "RFI";

      return (
        <>
          <span> replied to </span>
          <span style={{ color: "#00bf3a" }}>{code}</span>
        </>
      );
    }

    case ActivityTypeEnum.EMAIL_INBOUND_REQUESTER: {
      const code =
        activity.relativeInfo?.__typename === "ActivityRelativeCardInfo"
          ? activity.relativeInfo.code
          : "RFI";

      return (
        <>
          <span>replied to comment on </span>
          <span style={{ color: "#00bf3a" }}>{code}</span>
        </>
      );
    }

    case ActivityTypeEnum.EMAIL_OUTBOUND_MENTION: {
      const messageData = activity.data;

      // FIXME: Create the actual activity on server side
      return (
        <>
          <div>{messageData.emailOutboundMentionInfo}</div>
        </>
      );
    }

    case ActivityTypeEnum.EMAIL_OUTBOUND_SUPPLIER: {
      const rfxInfo = activity.data.rfxInfo;
      const code = rfxInfo ? `${rfxInfo?.type}-${rfxInfo?.code}` : undefined;

      return (
        <>
          <span>submitted an offer for </span>
          <span style={{ color: "#00bf3a" }}>{code}</span>
        </>
      );
    }

    case ActivityTypeEnum.CREATE_APPROVAL_WORKFLOW: {
      const messageData = activity.data;

      return (
        <>
          Created a new Approval Workflow draft{" "}
          <strong>{`AWF-${messageData.approvalWorkflowInfo.code}`}</strong>
        </>
      );
    }

    case ActivityTypeEnum.UPDATE_APPROVAL_WORKFLOW: {
      const messageData = activity.data;

      const workflowStatus = messageData.approvalWorkflowInfo.status;
      const workflowStats = messageData.approvalWorkflowStats;

      switch (workflowStatus) {
        case ApprovalWorkflowStatusEnum.IN_PROGRESS: {
          return (
            <>
              Sent{" "}
              <Link
                data-testid="09e2742be8"
                to={messageData.relativeCardInfo.url}
              >
                <strong>{messageData.relativeCardInfo.code}</strong>
              </Link>{" "}
              {`"${messageData.relativeCardInfo.name}"`} to Approval Workflow (
              <strong>{`AWF-${messageData.approvalWorkflowInfo.code}`}</strong>){" "}
              {workflowStats.approvedRequestCount}/
              {workflowStats.totalRequestCount}
            </>
          );
        }

        // TODO: add cancelling reason
        case ApprovalWorkflowStatusEnum.CANCELLED: {
          return (
            <>
              <strong>{`AWF-${messageData.approvalWorkflowInfo.code}`}</strong>{" "}
              <Link
                data-testid="a42efae874"
                to={messageData.relativeCardInfo.url}
              >
                <strong>{messageData.relativeCardInfo.code}</strong>
              </Link>{" "}
              {`"${messageData.relativeCardInfo.name}"`} got{" "}
              <span>Cancelled</span>
            </>
          );
        }

        case ApprovalWorkflowStatusEnum.APPROVED: {
          return (
            <>
              <strong>{`AWF-${messageData.approvalWorkflowInfo.code}`}</strong>{" "}
              <Link
                data-testid="a42efae874"
                to={messageData.relativeCardInfo.url}
              >
                <strong>{messageData.relativeCardInfo.code}</strong>
              </Link>{" "}
              {`"${messageData.relativeCardInfo.name}"`} got{" "}
              <span>Approved!</span>
            </>
          );
        }

        case ApprovalWorkflowStatusEnum.DECLINED: {
          return (
            <>
              <Link
                data-testid="170cd8a0fa"
                to={messageData.relativeCardInfo.url}
              >
                <strong>{messageData.relativeCardInfo.code}</strong>
              </Link>{" "}
              {`"${messageData.relativeCardInfo.name}"`}
              <strong>
                {" "}
                {`AWF-${messageData.approvalWorkflowInfo.code}`}
              </strong>{" "}
              got <span>Declined</span> with comment{" "}
              {messageData.approvalWorkflowInfo.comment}.
            </>
          );
        }

        case ApprovalWorkflowStatusEnum.DRAFT: {
          return (
            <>
              Updated Approval Workflow{" "}
              <strong> AWF-{messageData.approvalWorkflowInfo.code}</strong> info
            </>
          );
        }

        default:
          return assertUnreachable(
            workflowStatus,
            `Unhandled workflow status: ${workflowStatus}. This should not happen.`,
          );
      }
    }

    case ActivityTypeEnum.CREATE_APPROVAL_REQUEST: {
      const messageData = activity.data;

      return (
        <>
          Added{" "}
          <strong>{`${messageData.approverInfo.firstName} ${messageData.approverInfo.lastName}`}</strong>{" "}
          as approver. Approval due date{" "}
          <strong>{`${formatDatetime(
            messageData.approvalRequestInfo.dueDate,
          )}`}</strong>
          .
        </>
      );
    }

    case ActivityTypeEnum.UPDATE_APPROVAL_REQUEST: {
      const messageData = activity.data;

      const {
        approvalWorkflowInfo,
        approvalRequestInfo,
        approvalWorkflowStats,
      } = messageData;

      return (
        <>
          <strong>AWF-{approvalWorkflowInfo.code}</strong> got decision{" "}
          <strong>{approvalRequestInfo.decision}</strong> (
          {approvalWorkflowStats.approvedRequestCount}/
          {approvalWorkflowStats.totalRequestCount})
          {approvalRequestInfo.approverComment && (
            <>{` with comment: "${approvalRequestInfo.approverComment}"`}</>
          )}
        </>
      );
    }

    case ActivityTypeEnum.ADD_ATTACHMENT: {
      return (
        <>
          Uploaded a new attachment
          <div className={styles["file"]}>
            {activity.data.url && (
              <a
                className={styles["attachment"]}
                href={activity.data.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={styles["filename"]}>
                  {activity.data.filename}
                </span>
                <OpenExternalIcon className={styles["open-external-icon"]} />
              </a>
            )}
          </div>
        </>
      );
    }

    case ActivityTypeEnum.CREATE_SUPPLIER: {
      return null;
    }

    // TODO: The following events are to be handled as they are implemented
    case ActivityTypeEnum.DRAG_ITEM: {
      return null;
    }
    case ActivityTypeEnum.ADD_ITEM: {
      const { name, addItemQuantity, addItemUnit } = activity.data;

      return (
        <>
          Added item <strong>{name}</strong> {addItemQuantity} {addItemUnit}
        </>
      );
    }
    case ActivityTypeEnum.ADD_ITEMS: {
      return null;
    }

    default:
      return null;
  }
}
